import { useCallback, useEffect, useState } from "react";
import apiClient from "../../../../../helpers/api-client";
import { UseVerityAssessmentResultProps } from "./useVerityAssessmentResult";
import { CoreFileItem } from "@verity/components/core";

type UseAssignmentResultProps = Pick<UseVerityAssessmentResultProps, "verityAssessment" | "session">;

export default function useAssignmentResult({ verityAssessment, session }: UseAssignmentResultProps) {
	const { sessionUuid } = session;
	const [loading, setLoading] = useState(false);
	const [instructorFiles, setInstructorFiles] = useState<CoreFileItem[]>([]);
	const [studentFiles, setStudentFiles] = useState<CoreFileItem[]>([]);

	const fetchInstructorFiles = useCallback(
		async (fileName: string): Promise<CoreFileItem | undefined> => {
			try {
				const files = await apiClient.proctoringConfiguration.getAssessmentResources(verityAssessment.quizId);
				setInstructorFiles(files);

				return files.find((file) => file.name === fileName);
			} catch (err) {
				console.error("Failed to fetch instructor files", err);
			}
		},
		[verityAssessment.quizId],
	);

	const fetchStudentFiles = useCallback(
		async (fileName: string): Promise<CoreFileItem | undefined> => {
			try {
				const files = await apiClient.proctoringSession.getAssessmentFileSubmissions(sessionUuid);
				setStudentFiles(files);

				return files.find((file) => file.name === fileName);
			} catch (err) {
				console.error("Failed to fetch student files", err);
			}
		},
		[sessionUuid],
	);

	useEffect(() => {
		// No uuid yet (the attempt list hasn't arrived) means there is nothing to fetch — without this the
		// reads below go out against `undefined` and fail silently in the catch.
		if (verityAssessment.type !== "assignment" || !sessionUuid) return;

		void (async () => {
			try {
				setLoading(true);
				const [studentFiles, instructorFiles] = await Promise.all([
					apiClient.proctoringSession.getAssessmentFileSubmissions(sessionUuid),
					apiClient.proctoringConfiguration.getAssessmentResources(verityAssessment.quizId),
				]);
				setInstructorFiles(instructorFiles);
				setStudentFiles(studentFiles);
			} catch (err) {
				console.error("Failed to fetch file submissions", err);
			} finally {
				setLoading(false);
			}
		})();
	}, [sessionUuid, verityAssessment.quizId, verityAssessment.type]);

	const shouldShowFileCountChip = verityAssessment.type === "assignment";
	const shouldShowInstructorFiles = instructorFiles.length > 0 && verityAssessment.type === "assignment";

	return {
		files: {
			instructor: instructorFiles,
			student: studentFiles,
		},
		display: {
			showFileCountChip: shouldShowFileCountChip,
			showInstructorFiles: shouldShowInstructorFiles,
		},
		fetch: {
			instructor: fetchInstructorFiles,
			student: fetchStudentFiles,
		},
		loading,
	};
}

export type UseAssignmentResultReturn = ReturnType<typeof useAssignmentResult>;
